import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CILogo } from '../../components/CILogo';
import { Shield, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginType, setLoginType] = useState('super'); // super | merchant

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = loginType === 'super' 
        ? '/api/admin/super/login' 
        : '/api/admin/merchant/login';

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store session
      sessionStorage.setItem('admin_token', data.session_token);
      sessionStorage.setItem('admin_user', JSON.stringify(data.user));

      toast.success(`Welcome back, ${data.user.name}!`);

      // Redirect based on role
      if (data.user.role === 'super_admin') {
        navigate('/admin/super');
      } else {
        navigate('/admin/merchant');
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0B1120' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <CILogo size="lg" showText={false} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-[#9CB0C7]">Commerce Index Administration</p>
        </div>

        {/* Login Type Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setLoginType('super')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              loginType === 'super'
                ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                : 'bg-[#111A2E] text-[#9CB0C7] border border-[#22304A] hover:bg-[#1A2437]'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Super Admin
          </button>
          <button
            onClick={() => setLoginType('merchant')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              loginType === 'merchant'
                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/40'
                : 'bg-[#111A2E] text-[#9CB0C7] border border-[#22304A] hover:bg-[#1A2437]'
            }`}
          >
            Merchant Admin
          </button>
        </div>

        {/* Login Form */}
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white placeholder-[#9CB0C7] focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/50"
                placeholder="admin@commerceindex.com"
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white placeholder-[#9CB0C7] focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/50 pr-12"
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CB0C7] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                loginType === 'super'
                  ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600'
                  : 'bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90'
              } disabled:opacity-50`}
              data-testid="admin-login-button"
            >
              {loading ? 'Signing in...' : `Sign in as ${loginType === 'super' ? 'Super' : 'Merchant'} Admin`}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#22304A]">
            <p className="text-center text-[#9CB0C7] text-sm">
              Regular merchant? <button onClick={() => navigate('/merchant/login')} className="text-[#3EF1C7] hover:underline">Use Magic Link Login</button>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-[#9CB0C7] hover:text-white text-sm"
          >
            ← Back to Commerce Index
          </button>
        </div>
      </div>
    </div>
  );
}
